import React, { useEffect, useRef, useState } from "react";
import FormContainer from "../components/FormContainer";
import {
  Row,
  Col,
  Card,
  Container,
  Navbar,
  Button,
  Carousel,
  Nav,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";
import { fetchReservationActive } from "../actions/reservationsActions";
import { fetchSubscriptionPackages } from "../actions/userActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { useMediaQuery } from "react-responsive";
import { useParams } from "react-router-dom";

import CreateAccountScreen from "./CreateAccountScreen";
import AccountRecovery from "./AccountRecovery";
import PasswordRecoveryChange from "./PasswordRecoveryChange";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../components/LanguageSelector";

/* ---------------- Layout ---------------- */
const FullHeightRow = styled(Row)`
  height: 100%;
  width: 100%;
  margin: 0 !important;
  padding: 0;
  scroll-margin-top: 65px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
const FullHeightContainer = styled(Container)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding-top: 40px;
`;
const LeftSection = styled(Col)`
  background: linear-gradient(225deg, #ffe9db 0%, #ffffff 100%);
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  @media (min-width: 769px) {
    height: 100vh;
    flex: 1.2;
  }
  @media (max-width: 768px) {
    width: 100vw;
    height: 100vh;
    flex: none;
  }
`;
const RightSection = styled(Col)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: #fff;
  @media (min-width: 769px) {
    height: 100vh;
    flex: 1;
  }
  @media (max-width: 768px) {
    width: 100vw;
    height: 100vh;
    flex: none;
  }
`;
const HeroContentWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  padding: 20px;
  margin: 0 auto;
`;
const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  margin-top: 20px;
  @media (max-width: 768px) {
    padding-top: 0;
    margin-top: 0;
  }
`;
const LogoImage = styled.img`
  max-width: 100px;
  height: auto;
`;
const SobreNosCard = styled(Container)`
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  text-align: start;
`;
const Ptext = styled.h4`
  font-style: bold;
  font-size: 20px;
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const bounce = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(6px)}}`;
const MobileArrowButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  &:focus,
  &:active {
    box-shadow: none;
    outline: none;
  }
  @media (min-width: 769px) {
    display: none;
  }
`;
const MobileArrowLabel = styled.span`
  font-size: 16px;
  color: #ee754c;
  font-weight: 600;
  margin-bottom: 4px;
`;
const MobileArrowIcon = styled(FontAwesomeIcon)`
  font-size: 48px;
  color: #ee754c;
  animation: ${bounce} 1.5s infinite;
`;

/* ---------------- Slideshow ---------------- */
const SlideshowContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  img {
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    max-height: 500px;
    object-fit: cover;
    width: 100%;
  }
  .carousel-control-next-icon,
  .carousel-control-prev-icon {
    background: none !important;
  }
  .carousel-control-prev::after,
  .carousel-control-next::after {
    font-size: 48px;
    color: #ee754c;
    font-weight: bold;
  }
  .carousel-control-prev::after {
    content: "‹";
  }
  .carousel-control-next::after {
    content: "›";
  }
  .carousel-indicators {
    display: none !important;
  }
`;
const LeftSectionCarousel = styled(Col)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  @media (min-width: 769px) {
    height: 100%;
  }
  @media (max-width: 768px) {
    width: 100vw;
    height: 100vh;
    flex: none;
  }
`;
const RightSectionCarousel = styled(Col)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  background: linear-gradient(225deg, #ffe9db 0%, #ffffff 100%);
  @media (min-width: 769px) {
    height: 100%;
  }
  @media (max-width: 768px) {
    width: 100vw;
    height: 100vh;
    flex: none;
  }
`;
const FullHeightRowCarousel = styled(Row)`
  width: 100%;
  margin: 0 !important;
  padding: 0;
  scroll-margin-top: 65px;
  @media (max-width: 768px) {
    flex-direction: column;
    min-height: 100vh;
  }
  @media (min-width: 769px) {
    height: 100vh;
  }
`;

/* ---------------- Plans ---------------- */
const palette = ["#9FBBC1", "#EE754C", "#99B0AD"];
const getPlanColor = (n) => palette[(Number(n) - 1) % palette.length];

const PlanCard = styled(Card)`
  border-radius: 20px;
  border: 1px solid #dee2e6;
  background: #fff;
  padding: 32px 24px;
  text-align: center;
  transition: transform 0.3s ease;
  min-height: 100%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  &:hover {
    transform: translateY(-5px);
  }
`;
const PlanName = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: ${({ color }) => `${color || "#333"} !important`};
  text-transform: uppercase;
  margin-bottom: 8px;
`;
const PlanDescription = styled.p`
  font-size: 14px;
  color: #444;
  margin-bottom: 24px;
`;
const PlanPrice = styled.div`
  font-size: 42px;
  font-weight: bold;
  color: ${({ color }) => color || "#333"};
`;
const PerMonth = styled.div`
  font-size: 14px;
  color: #99b0ad;
  margin-bottom: 20px;
`;
const FeatureList = styled.ul`
  list-style: none;
  padding-left: 0;
  text-align: left;
  margin-top: 10px;
  li {
    display: flex;
    align-items: flex-start;
    font-size: 14px;
    color: #333;
    margin-bottom: 10px;
    font-family: "Kanit", sans-serif;
  }
  li svg {
    color: ${({ iconColor }) => iconColor || "#ee754c"};
    margin-right: 10px;
    font-size: 14px;
    margin-top: 3px;
  }
`;
const SubscribeButton = styled(Button)`
  background-color: ${({ color }) => color || "#ee754c"};
  border: none;
  border-radius: 999px;
  padding: 10px 28px;
  font-weight: 700;
  font-size: 14px;
  margin: 16px auto 24px;
  color: #fff;
  &:hover {
    background-color: ${({ color }) => color || "#ee754c"};
    opacity: 0.9;
  }
  &:focus,
  &:active {
    background-color: ${({ color }) => color || "#ee754c"} !important;
    box-shadow: none !important;
    outline: none !important;
    border: none !important;
    color: #fff !important;
  }
`;
const DiscountNote = styled.div`
  font-size: 13px;
  color: #28a745;
  font-style: italic;
  margin-top: -10px;
  margin-bottom: 12px;
`;
const PriceLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
`;

const ButtonPlans = styled.div`
  padding: 10px 24px;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  background-color: ${(p) => (p.isSelected ? "#EE754C" : "#fff")};
  color: ${(p) => (p.isSelected ? "#fff" : "#EE754C")};
  border: 2px solid #ee754c;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  margin: 5px;
  box-shadow: ${(p) =>
    p.isSelected ? "0 6px 12px rgba(238,117,76,0.2)" : "none"};
  &:hover {
    background-color: #ee561b;
    color: #fff;
  }
`;

/* Navbar */
const NavbarCustom = styled(Navbar)`
  background: #fff;
  padding: 3px 15px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 1050;
  @media (max-width: 768px) {
    padding: 2px 10px;
  }
`;
const NavLinkStyled = styled(Nav.Link)`
  color: #ee754c !important;
  font-weight: 600;
  font-size: 16px;
  padding: 8px 12px;
  border-radius: 20px;
  border: 2px solid transparent;
  background: transparent;
  transition: all 0.2s;
  &:hover {
    background: rgba(238, 117, 76, 0.1);
    color: #ee561b !important;
  }
`;
const NavbarLanguageSelector = styled(LanguageSelector)`
  margin-left: 4px;

  @media (max-width: 768px) {
    margin: 6px 0 0;
    align-self: flex-start;
  }
`;
const CustomToggle = styled(Navbar.Toggle)`
  border: none !important;
  box-shadow: none !important;
  padding: 0;
  outline: none !important;
  &:focus {
    box-shadow: none !important;
    outline: none !important;
  }
  .navbar-toggler-icon {
    background-image: none;
    width: 24px;
    height: 2px;
    background: #ee754c;
    position: relative;
  }
  .navbar-toggler-icon::before,
  .navbar-toggler-icon::after {
    content: "";
    position: absolute;
    width: 24px;
    height: 2px;
    background: #ee754c;
    left: 0;
  }
  .navbar-toggler-icon::before {
    top: -6px;
  }
  .navbar-toggler-icon::after {
    top: 6px;
  }
`;

/* ---------- Helpers for features (semicolon-only) ---------- */
const splitOffersSafely = (s) =>
  (s || "")
    .split(/\s*;\s*/g) // ONLY semicolons between items
    .map((x) => x.trim())
    .filter(Boolean);

const splitTitleAndDesc = (item) => {
  const idx = item.indexOf(":");
  if (idx === -1) return { title: item.trim(), desc: "" };
  return { title: item.slice(0, idx).trim(), desc: item.slice(idx + 1).trim() };
};

/* ---------------- Component ---------------- */
const LoginScreen = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formRef = useRef(null);
  const slideshowRef = useRef(null);
  const planosRef = useRef(null);
  const topRef = useRef(null);

  const { recovery_code } = useParams();
  const [showRegister, setShowRegister] = useState(false);
  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);
  const [showMonthly, setShowMonthly] = useState(true);
  const [showSubscribeNotice, setShowSubscribeNotice] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const { getSubscriptionPackages } = useSelector(
    (state) => state.subscriptionPackagesStore,
  );

  // robust buckets (so 28–31 days still counts as monthly, etc.)
  const monthlyPlans =
    getSubscriptionPackages
      ?.filter(
        (p) => Number(p.number_of_days) > 0 && Number(p.number_of_days) < 90,
      )
      .sort((a, b) => a.subscription_number - b.subscription_number) || [];
  const yearlyPlans =
    getSubscriptionPackages
      ?.filter((p) => Number(p.number_of_days) >= 300)
      .sort((a, b) => a.subscription_number - b.subscription_number) || [];

  useEffect(() => {
    dispatch(fetchSubscriptionPackages());
  }, [dispatch]);
  useEffect(() => {
    dispatch(fetchReservationActive());
  }, [dispatch]);

  const isSmallScreen = useMediaQuery({ query: "(max-width: 768px)" });

  const handleLoginClick = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (recovery_code && isSmallScreen && formRef.current) {
      setTimeout(() => {
        formRef.current.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, [recovery_code, isSmallScreen]);

  const handleSubscribeClick = () => {
    setShowSubscribeNotice(true);
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleNavClick = (ref) => {
    setShowMenu(false);
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth" });
    }, 300);
  };

  return (
    <div>
      <NavbarCustom expand="md" expanded={showMenu} onToggle={setShowMenu}>
        <Navbar.Brand
          onClick={() => topRef.current?.scrollIntoView({ behavior: "smooth" })}
          style={{
            fontWeight: "bold",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <LogoImage src="/static/logo.png" alt={t("loginScreen.alt.logo")} />
        </Navbar.Brand>

        <CustomToggle
          aria-controls="main-navbar"
          aria-label={t("loginScreen.nav.menu")}
        >
          <span className="navbar-toggler-icon" />
        </CustomToggle>

        <Navbar.Collapse id="main-navbar">
          <Nav
            className="ms-auto"
            style={{ display: "flex", alignItems: "center", gap: "12px" }}
          >
            <NavLinkStyled onClick={() => handleNavClick(topRef)}>
              {t("loginScreen.nav.home")}
            </NavLinkStyled>
            <NavLinkStyled onClick={() => handleNavClick(slideshowRef)}>
              {t("loginScreen.nav.demo")}
            </NavLinkStyled>
            <NavLinkStyled onClick={() => handleNavClick(planosRef)}>
              {t("loginScreen.nav.plans")}
            </NavLinkStyled>
            <NavLinkStyled onClick={() => handleNavClick(formRef)}>
              {t("auth.login")}
            </NavLinkStyled>
            <NavbarLanguageSelector />
          </Nav>
        </Navbar.Collapse>
      </NavbarCustom>

      <FullHeightRow ref={topRef} style={{ scrollMarginTop: "65px" }}>
        {/* Left: pitch */}
        <LeftSection md={7} xs={12}>
          <HeroContentWrapper>
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "100%", marginBottom: "40px" }}
            >
              <SobreNosCard>
                <Card.Body>
                  <h2 className="slide-title" style={{ marginBottom: "20px" }}>
                    {t("loginScreen.hero.title")}
                  </h2>
                  <Ptext>
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{ color: "#a3a7ae" }}
                    />{" "}
                    {t("loginScreen.hero.acceptsReservations")}
                  </Ptext>
                  <Ptext>
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{ color: "#a3a7ae" }}
                    />{" "}
                    {t("loginScreen.hero.reservationOptimization")}
                  </Ptext>
                  <Ptext>
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{ color: "#a3a7ae" }}
                    />{" "}
                    {t("loginScreen.hero.noShowPrevention")}
                  </Ptext>
                  <Ptext>
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{ color: "#a3a7ae" }}
                    />{" "}
                    {t("loginScreen.hero.whatsappNotifications")}
                  </Ptext>
                </Card.Body>
              </SobreNosCard>
            </div>
            <MobileArrowButton
              onClick={() =>
                slideshowRef.current?.scrollIntoView({ behavior: "smooth" })
              }
            >
              <MobileArrowLabel>
                {t("loginScreen.hero.moreInformation")}
              </MobileArrowLabel>
              <MobileArrowIcon icon={faChevronDown} />
            </MobileArrowButton>
          </HeroContentWrapper>
        </LeftSection>

        {/* Right: auth */}
        <RightSection
          xs={12}
          md={5}
          ref={formRef}
          style={{ scrollMarginTop: "65px" }}
        >
          <ContentWrapper>
            <img
              src="/static/logo.png"
              alt={t("loginScreen.alt.companyLogo")}
              style={{ maxWidth: "220px", height: "auto" }}
            />
            {recovery_code ? (
              <PasswordRecoveryChange code={recovery_code} />
            ) : showRegister ? (
              <CreateAccountScreen
                onSwitchToLogin={() => setShowRegister(false)}
              />
            ) : showPasswordRecovery ? (
              <AccountRecovery
                onSwitchToLogin={() => setShowPasswordRecovery(false)}
              />
            ) : (
              <>
                {showSubscribeNotice && (
                  <div
                    style={{
                      backgroundColor: "#fee2e2",
                      color: "#b91c1c",
                      padding: "10px 20px",
                      borderRadius: "8px",
                      marginBottom: "16px",
                      textAlign: "center",
                      fontWeight: "600",
                      marginTop: "10px",
                    }}
                  >
                    {t("loginScreen.auth.subscribeNotice")}
                  </div>
                )}
                <FormContainer
                  onSwitchToRegister={() => setShowRegister(true)}
                  onSwitchToRecovery={() => setShowPasswordRecovery(true)}
                />
              </>
            )}
          </ContentWrapper>
        </RightSection>
      </FullHeightRow>

      {/* Slideshow */}
      <FullHeightRowCarousel
        ref={slideshowRef}
        style={{ scrollMarginTop: "65px" }}
      >
        <LeftSectionCarousel xs={12} md={5}>
          <ContentWrapper>
            <h2
              className="slide-title"
              style={{ marginBottom: "20px", textAlign: "center" }}
            >
              {t("loginScreen.demo.title")}
            </h2>
            <p style={{ textAlign: "center", maxWidth: "400px" }}>
              {t("loginScreen.demo.description")}
            </p>
          </ContentWrapper>
        </LeftSectionCarousel>

        <RightSectionCarousel md={7} xs={12}>
          <SlideshowContainer>
            <Carousel>
              <Carousel.Item>
                <img
                  className="d-block w-100"
                  src="/static/img/1.png"
                  alt={t("loginScreen.alt.demoScreenshot", { number: 1 })}
                />
              </Carousel.Item>
              <Carousel.Item>
                <img
                  className="d-block w-100"
                  src="/static/img/2.png"
                  alt={t("loginScreen.alt.demoScreenshot", { number: 2 })}
                />
              </Carousel.Item>
              <Carousel.Item>
                <img
                  className="d-block w-100"
                  src="/static/img/3.png"
                  alt={t("loginScreen.alt.demoScreenshot", { number: 3 })}
                />
              </Carousel.Item>
            </Carousel>
          </SlideshowContainer>
        </RightSectionCarousel>
      </FullHeightRowCarousel>

      {/* Plans */}
      {getSubscriptionPackages && (
        <FullHeightContainer
          ref={planosRef}
          style={{ scrollMarginTop: "65px" }}
        >
          <h2
            className="slide-title"
            style={{ marginBottom: "20px", textAlign: "Center" }}
          >
            {t("loginScreen.plans.title")}
          </h2>

          <div className="d-flex justify-content-center mb-4">
            <ButtonPlans
              isSelected={showMonthly}
              onClick={() => setShowMonthly(true)}
            >
              {t("loginScreen.plans.monthly")}
            </ButtonPlans>
            <ButtonPlans
              isSelected={!showMonthly}
              onClick={() => setShowMonthly(false)}
            >
              {t("loginScreen.plans.yearly")}
            </ButtonPlans>
          </div>

          <Row className="justify-content-center">
            {(showMonthly ? monthlyPlans : yearlyPlans).map((plan, idx) => {
              const color = getPlanColor(plan.subscription_number);
              const offers = splitOffersSafely(plan.offers);
              return (
                <Col
                  xs={12}
                  md={4}
                  key={plan.subscription_number || idx}
                  className="mb-4 d-flex"
                >
                  <PlanCard>
                    <PlanName color={color}>{plan.name}</PlanName>
                    <PlanDescription>
                      {t("loginScreen.plans.description")}
                    </PlanDescription>
                    <PriceLine>
                      <PlanPrice color={color}>
                        {plan.monthly_value} €
                      </PlanPrice>
                      <PerMonth>{t("loginScreen.plans.perMonth")}</PerMonth>
                    </PriceLine>
                    {Number(plan.number_of_days) === 365 && (
                      <DiscountNote>
                        {t("loginScreen.plans.yearlyDiscount")}
                      </DiscountNote>
                    )}

                    <SubscribeButton
                      color={color}
                      onClick={handleSubscribeClick}
                    >
                      {t("loginScreen.plans.subscribe")}
                    </SubscribeButton>

                    {offers.length > 0 && (
                      <FeatureList iconColor={color}>
                        {offers.map((raw, i) => {
                          const { title, desc } = splitTitleAndDesc(raw);
                          return (
                            <li key={i}>
                              <FontAwesomeIcon icon={faCircleCheck} />
                              <div>
                                <div style={{ fontWeight: 700 }}>{title}</div>
                                {desc && <div>{desc}</div>}
                              </div>
                            </li>
                          );
                        })}
                      </FeatureList>
                    )}
                  </PlanCard>
                </Col>
              );
            })}
          </Row>
        </FullHeightContainer>
      )}
    </div>
  );
};

export default LoginScreen;
